import { useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import { capitalize } from "lodash";

import Widget from "../../../../forms/components/Widget";
import { FAVORITE_QUERIES_KEY } from "../../../../hooks";
import api from "../../../../services/api";
import { show } from "../../../../notifications/notifications";
import { advancedSearchKeys } from "../../../samples/queryKeys";
import SearchHistoryItem from "../SearchHistoryItem";
import { IEditFavouriteProps } from "./@types/EditFavoriteModal";
import { NotificationsLevel } from "../../../../lib/enums";
import { ErrorResponse } from "../../../../requests/@types";

const EditFavourite = ({
  id,
  name,
  content,
  formatErrorMessage = capitalize,
  onClose
}: IEditFavouriteProps) => {
  const queryClient = useQueryClient();
  const [errors, setErrors] = useState<Array<string> | undefined>(undefined);

  const handleEditFavourite: React.FormEventHandler<
    HTMLFormElement
  > = event => {
    event.preventDefault();
    const editPayload = {
      id,
      name: event.currentTarget.editedName.value
    };
    setErrors(undefined);
    api
      .editFavourite(editPayload)
      .then(() => {
        show({
          level: NotificationsLevel.SUCCESS,
          message: "Favorite query successfully edited."
        });
        queryClient.invalidateQueries({ queryKey: FAVORITE_QUERIES_KEY });
        onClose();
      })
      .catch((error: ErrorResponse<{ name: Array<string> }>) => {
        const { name: errorName } = error.data || {};
        if (errorName?.length) {
          setErrors(errorName?.map(e => formatErrorMessage(e)));
        } else {
          show({
            level: NotificationsLevel.ERROR,
            message:
              "Oops! There was an error while adding new favorite query. Please try again."
          });
        }
      });
  };

  const handleRemoveFavourite: React.MouseEventHandler<
    HTMLButtonElement
  > = e => {
    e.preventDefault();
    return api
      .deleteFavourite(id)
      .then(() => {
        show({
          level: "success",
          message: `Favorite query "${name}" successfully deleted.`
        });
        queryClient.invalidateQueries({ queryKey: FAVORITE_QUERIES_KEY });
        queryClient.invalidateQueries({
          queryKey: advancedSearchKeys.fetchUserData
        });

        onClose();
      })
      .catch(() => {
        show({
          level: "error",
          message: `Oops! Error while deleting favorite "${name}" query. Please try again.`
        });
        onClose();
      });
  };

  return (
    <Modal show size="lg">
      <Modal.Header>
        <Modal.Title>Edit Favorite</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleEditFavourite}>
        <Modal.Body>
          <Widget
            name="editedName"
            label="Name"
            defaultValue={name}
            onChange={() => null}
            errors={errors}
          />
          <Form.Group as={Row}>
            <Col xs={3}>
              <Form.Label>Query</Form.Label>
            </Col>
            <Col xs={9}>
              <SearchHistoryItem history={content} />
            </Col>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            variant="prompt-secondary"
            onClick={handleRemoveFavourite}
          >
            Delete
          </Button>
          <Button type="button" variant="prompt-secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="prompt-primary">
            Save
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditFavourite;
